/* ------------------------------------------------------------------------------------

  Croner - MIT License - Hexagon <github.com/Hexagon>

  Pure JavaScript Isomorphic cron parser and scheduler without dependencies.

  ------------------------------------------------------------------------------------

  License:

	Copyright (c) 2015-2021 Hexagon <github.com/Hexagon>

	Permission is hereby granted, free of charge, to any person obtaining a copy
	of this software and associated documentation files (the "Software"), to deal
	in the Software without restriction, including without limitation the rights
	to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
	copies of the Software, and to permit persons to whom the Software is
	furnished to do so, subject to the following conditions:
	The above copyright notice and this permission notice shall be included in
	all copies or substantial portions of the Software.
	THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
	IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
	FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.  IN NO EVENT SHALL THE
	AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
	LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
	OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
	THE SOFTWARE.

  ------------------------------------------------------------------------------------  */
import { CronDate } from "./date.js";
import { CronPattern } from "./pattern.js";
import { CronOptions } from "./options.js";

/**
 * Many JS engines stores the delay as a 32-bit signed integer internally.
 * This causes an integer overflow when using delays larger than 2147483647, 
 * resulting in the timeout being executed immediately.
 * 
 * All JS engines implements an immediate execution of delays larger that a 32-bit 
 * int to keep the behaviour concistent. 
 * 
 * @type {number}
 */
const maxDelay = Math.pow(2, 32 - 1) - 1;
	
/**
 * Cron entrypoint
 * 
 * @constructor
 * @param {string|Date} pattern - Input pattern, input date, or input ISO 8601 time string
 * @param {CronOptions|Function} [fnOrOptions1] - Options or function to be run each iteration of pattern
 * @param {CronOptions|Function} [fnOrOptions2] - Options or function to be run each iteration of pattern
 * @returns {Cron}
 */
function Cron (pattern, fnOrOptions1, fnOrOptions2) {
	
	// Optional "new" keyword
	if( !(this instanceof Cron) ) {
		return new Cron(pattern, fnOrOptions1, fnOrOptions2);
	}
	
	// Make options and func optional and interchangable
	let options, func;

	if( typeof fnOrOptions1 === "function" ) {
		func = fnOrOptions1;
	} else if( typeof fnOrOptions1 === "object" ) {
		options = fnOrOptions1;
	} else if( fnOrOptions1 !== void 0) {
		throw new Error("Cron: Invalid argument passed for optionsIn. Should be one of function, or object (options).");
	}

	if( typeof fnOrOptions2 === "function" ) {
		func = fnOrOptions2;
	} else if( typeof fnOrOptions2 === "object" ) {
		options = fnOrOptions2;
	} else if( fnOrOptions2 !== void 0) {
		throw new Error("Cron: Invalid argument passed for funcIn. Should be one of function, or object (options).");
	}
	
	/** @type {CronOptions} */
	this.options = CronOptions(options);
	
	/** @type {CronDate|undefined} */
	this.once = void 0;
	
	/** @type {CronPattern|undefined} */
	this.pattern = void 0;
	
	// Check if we got a date, or a pattern supplied as first argument
	// Then set either this.once or this.pattern
	if (pattern && (pattern instanceof Date || ((typeof pattern === "string") && pattern.indexOf(":") > 0))) {
		this.once = new CronDate(pattern, this.options.timezone);
	} else {
		this.pattern = new CronPattern(pattern, this.options.timezone);
	}
	
	// Allow shorthand scheduling
	if( func !== void 0 ) {
		this.fn = func;
		this.schedule();
	}
	
	return this;
	
}
	
/**
 * Find next runtime, based on supplied date. Strips milliseconds.
 * 
 * @param {CronDate|Date|string} [prev] - Date to start from
 * @returns {Date | null} - Next run time
 */
Cron.prototype.next = function (prev) {
	const next = this._next(prev);
	return next ? next.getDate() : null;
};
	
/**
 * Find next n runs, based on supplied date. Strips milliseconds.
 * 
 * @param {number} n - Number of runs to enumerate
 * @param {Date|string} [previous] - Date to start from
 * @returns {Date[]} - Next n run times
 */
Cron.prototype.enumerate = function (n, previous) {
	const enumeration = [];
	let prev = previous || this.previousrun;
	while(n-- && (prev = this.next(prev))) {
		enumeration.push(prev);
	}
	
	return enumeration;
};
	
/**
 * Is running?
 * @public
 * 
 * @returns {boolean} - Running or not
 */
Cron.prototype.running = function () {
	const msLeft = this.msToNext(this.previousrun);
	const running = !this.options.paused && this.fn !== void 0;
	return msLeft !== null && running;
};
	
/**
 * Return previous run time
 * @public
 * 
 * @returns {Date | null} - Previous run time
 */
Cron.prototype.previous = function () {
	return this.previousrun ? this.previousrun.getDate() : null;
};
	
/**
 * Returns number of milliseconds to next run
 * @public
 * 
 * @param {CronDate|Date|string} [prev] - Starting date, defaults to now - minimum interval
 * @returns {number | null}
 */
Cron.prototype.msToNext = function (prev) {

	// Get next run time
	const next = this._next(prev || this.previousrun);

	// Default previous for millisecond calculation
	prev = new CronDate(prev, this.options.timezone);

	if( next ) {
		return (next.getTime(true) - prev.getTime(true));
	} else {
		return null;
	}
};
	
/**
 * Stop execution 
 * @public
 */
Cron.prototype.stop = function () {
	this.options.kill = true;
	// Stop any awaiting call
	if( this.currentTimeout ) {
		clearTimeout( this.currentTimeout );
	}
};
	
/**
 * Pause execution
 * @public
 * 
 * @returns {boolean} - Wether pause was successful
 */
Cron.prototype.pause = function () {
	return (this.options.paused = true) && !this.options.kill;
};
	
/**
 * Resume execution
 * @public
 * 
 * @returns {boolean} - Wether resume was successful
 */
Cron.prototype.resume = function () {
	return !(this.options.paused = false) && !this.options.kill;
};
	
/**
 * Schedule a new job
 * @public
 * 
 * @param {Function} func - Function to be run each iteration of pattern
 * @param {Date} [partial] - Internal function indicating a partial run
 * @returns {Cron}
 */
Cron.prototype.schedule = function (func, partial) {
	
	// If a function is already scheduled, bail out
	if (func && this.fn) {
		throw new Error("Cron: It is not allowed to schedule two functions using the same Croner instance.");
		
		// Update function if passed
	} else if (func) {
		this.fn = func;
	}
	
	// Get ms to next run, bail out early if waitMs is null (no next run)
	let	waitMs = this.msToNext(partial ? partial : this.previousrun);
	const target = this.next(partial ? partial :  this.previousrun);

	if  ( waitMs === null )  return this;
	
	// setTimeout cant handle more than Math.pow(2, 32 - 1) - 1 ms
	if( waitMs > maxDelay ) {
		waitMs = maxDelay;
	}
	
	// Ok, go!
	this.currentTimeout = setTimeout(() => {
	
		const now = new Date();

		if( waitMs !== maxDelay && !this.options.paused && now.getTime() >= target ) {
	
			this.options.maxRuns--;
	
			// Always catch errors, but only re-throw if options.catch is not set
			if (this.options.catch) {
				try {
					this.fn(this, this.options.context);
				} catch (_e) {
					// Ignore
				}
			} else {
				this.fn(this, this.options.context);
			}
	
			// Set previous run to now
			this.previousrun = new CronDate(void 0, this.options.timezone);
	
			// Recurse
			this.schedule();
			
		} else {
			// Partial
			this.schedule(undefined, now);
		}
	
	
	}, waitMs);
		
	return this;
	
};

	
/**
 * Internal version of next. Cron needs millseconds internally, hence _next.
 * @private
 * 
 * @param {CronDate|Date|string} prev - PreviousRun
 * @returns {CronDate | null} - Next run time
 */
Cron.prototype._next = function (prev) {

	const hasPreviousRun = (prev || this.previousrun) ? true : false;

	// Ensure previous run is a CronDate
	prev = new CronDate(prev, this.options.timezone);
	
	// Previous run should never be before startAt
	if( this.options.startAt && prev && prev.getTime(true) < this.options.startAt.getTime(true) ) {
		prev = this.options.startAt;
	}

	// Calculate next run according to pattern or one-off timestamp, pass actual previous run to increment
	const 
		nextRun = this.once || new CronDate(prev, this.options.timezone).increment(this.pattern, this.options, hasPreviousRun);
	
	if (this.once && this.once.getTime(true) <= prev.getTime(true)) {
		return null;
  
	} else if ((nextRun === null) ||
		(this.options.maxRuns <= 0) ||	
		(this.options.kill) ||
		(this.options.stopAt && nextRun.getTime(true) >= this.options.stopAt.getTime(true) )) {
		return null;

	} else {
		// All seem good, return next run
		return nextRun;
  
	}
		
};

Cron.Cron = Cron;
export default Cron;
export { Cron };

// denoCacheMetadata={"headers":{"server-timing":"fetchSource;dur=4","cross-origin-embedder-policy":"same-origin","content-security-policy":"default-src 'none'; style-src 'unsafe-inline'; sandbox","etag":"W/\"dec2f244d06ca5ecee68c0d0406c0f54\"","x-cache":"Hit from cloudfront","cross-origin-resource-policy":"same-origin","x-content-type-options":"nosniff","referrer-policy":"strict-origin-when-cross-origin","via":"http/2 edgeproxy-h","server":"deno/gcp-us-east4","vary":"Accept-Encoding, Origin","date":"Tue, 04 Jun 2024 18:27:31 GMT","x-amz-version-id":"6HPnQxw6zhjV7Tc6_zTOxoggvSBCnLLp","cache-control":"public, max-age=31536000, immutable","content-type":"application/javascript","access-control-allow-origin":"*","last-modified":"Fri, 16 Sep 2022 08:42:54 GMT","x-amz-replication-status":"COMPLETED","x-amz-cf-pop":"IAD61-P1","age":"14090510","cross-origin-opener-policy":"same-origin","strict-transport-security":"max-age=63072000; includeSubDomains; preload","x-amz-cf-id":"yZOiRqr1NuKGxbq8MoNkR3bgrV92F2CQbmhgogodNcr0wndOADgjQQ==","x-frame-options":"DENY"},"url":"https://deno.land/x/croner@4.4.1/src/croner.js","time":1731616160}